// 服装管理服务 - 处理角色换装功能
import enhancedStorageService from './enhancedStorageService.js'
import { DoubaoImageServiceV2 } from './doubaoImageServiceV2.js'
import imageUploadService from './imageUploadService.js'
import { v4 as uuidv4 } from 'uuid'

class ClothingService {
  constructor() {
    this.db = null
  }

  /**
   * 确保数据库已初始化
   */
  async ensureDB() {
    if (!this.db) {
      this.db = await enhancedStorageService.ensureDB()
    }
    
    // 检查objectStore是否存在
    if (this.db && !this.db.objectStoreNames.contains('characterClothing')) {
      console.warn('⚠️ 数据库缺少 characterClothing 存储，需要升级...')
      // 关闭当前数据库连接
      if (this.db) {
        this.db.close()
        this.db = null
      }
      // 清除 enhancedStorageService 的数据库连接
      enhancedStorageService.db = null
      enhancedStorageService.initPromise = enhancedStorageService.initDB()
      
      // 重新初始化
      this.db = await enhancedStorageService.ensureDB()
      
      // 再次检查
      if (!this.db.objectStoreNames.contains('characterClothing')) {
        throw new Error('数据库升级失败，请手动刷新页面（Ctrl+F5 或 Cmd+Shift+R）')
      }
    }
    
    return this.db
  }

  /**
   * 生成角色+服装的完整立绘（文生图模式 - 旧方法保留兼容）
   * @param {Object} character - 角色信息
   * @param {Object} clothing - 服装信息
   * @returns {string} 生成的图片URL
   */
  async generateClothedAvatar(character, clothing) {
    try {
      console.log('🎨 开始生成换装图（文生图模式），角色:', character.name, '服装:', clothing.name || clothing.description)
      
      // 构建换装提示词（强调保持角色一致性）
      const prompt = this.buildClothingPrompt(character, clothing)
      
      // 调用AI生成图片 - 使用16:9比例与场景图一致
      const imageUrl = await DoubaoImageServiceV2.generateImage(prompt, {
        aspectRatio: '16:9',
        maxImages: 1
      })
      
      console.log('✅ 换装图生成成功:', imageUrl)
      return imageUrl
    } catch (error) {
      console.error('❌ 生成换装立绘失败:', error)
      throw error
    }
  }

  /**
   * 使用图生图生成换装立绘（新方法 - 支持参考图）
   * @param {Object} character - 角色信息
   * @param {Object} clothing - 服装信息
   * @param {Object} options - 选项
   * @param {string} options.mode - 模式：'single'（单图参考）或 'dual'（双图融合）
   * @param {string} options.referenceClothingImage - 服装参考图（双图模式需要）
   * @param {Object} options.selectedReferenceClothing - 选中的参考服装（可选，用于替代默认立绘）
   * @param {string} options.uploadedReferenceImage - 上传的参考图（可选，优先级最高）
   * @returns {Promise<string>} 生成的图片URL
   */
  async generateClothedAvatarWithReference(character, clothing, options = {}) {
    try {
      const { 
        mode = 'single', 
        referenceClothingImage = null, 
        selectedReferenceClothing = null,
        uploadedReferenceImage = null 
      } = options
      
      console.log('🎨 开始生成换装图（图生图模式）:', {
        角色: character.name,
        服装: clothing.name || clothing.description,
        模式: mode === 'single' ? '单图参考' : '双图融合',
        参考来源: uploadedReferenceImage ? '上传的图片' : 
                  selectedReferenceClothing ? selectedReferenceClothing.name : 
                  '默认立绘'
      })

      // 获取角色参考立绘（优先级：上传图 > 选中服装 > 默认立绘）
      let characterAvatar
      if (uploadedReferenceImage) {
        // 1. 优先使用上传的参考图
        characterAvatar = uploadedReferenceImage
        console.log('📸 使用上传的参考图')
      } else if (selectedReferenceClothing) {
        // 2. 使用选中的服装作为参考
        if (selectedReferenceClothing.imageBlob) {
          characterAvatar = URL.createObjectURL(selectedReferenceClothing.imageBlob)
        } else {
          characterAvatar = selectedReferenceClothing.generatedImageUrl || selectedReferenceClothing.imageUrl
        }
        console.log('📸 使用选中的服装作为参考:', selectedReferenceClothing.name)
      } else {
        // 3. 使用默认立绘
        characterAvatar = character.originalAvatar || character.avatar
        console.log('📸 使用默认立绘作为参考')
      }
      
      if (!characterAvatar) {
        throw new Error('没有可用的参考立绘，无法使用图生图模式')
      }

      // 转换URL为API可接受的格式（处理Blob URL）
      let referenceImages
      if (mode === 'single') {
        // 单图模式：只使用角色立绘
        console.log('📸 单图模式：使用角色原始立绘')
        referenceImages = await imageUploadService.convertToApiFormat(characterAvatar)
      } else if (mode === 'dual') {
        // 双图模式：角色立绘 + 服装参考图
        if (!referenceClothingImage) {
          throw new Error('双图模式需要提供服装参考图')
        }
        console.log('📸 双图模式：角色立绘 + 服装参考图')
        const convertedAvatar = await imageUploadService.convertToApiFormat(characterAvatar)
        const convertedClothing = await imageUploadService.convertToApiFormat(referenceClothingImage)
        referenceImages = [convertedAvatar, convertedClothing]
      } else {
        throw new Error('无效的模式：' + mode)
      }

      // 构建图生图提示词
      const prompt = this.buildImageToImagePrompt(character, clothing, mode)

      // 调用图生图API
      const imageUrl = await DoubaoImageServiceV2.generateImageWithReference(
        prompt,
        referenceImages,
        {
          size: '2K',
          watermark: false // 不添加水印
        }
      )

      console.log('✅ 图生图换装成功:', imageUrl)
      return imageUrl
    } catch (error) {
      console.error('❌ 图生图换装失败:', error)
      throw error
    }
  }

  /**
   * 构建图生图提示词
   * @param {Object} character - 角色信息
   * @param {Object} clothing - 服装信息
   * @param {string} mode - 模式：'single' 或 'dual'
   * @returns {string} 提示词
   */
  buildImageToImagePrompt(character, clothing, mode = 'single') {
    const name = character.name || '角色'
    let clothingDescription = clothing.description || clothing.name || '新服装'
    
    // 过滤和优化服装描述，避免触发内容审核
    clothingDescription = this.sanitizeClothingDescription(clothingDescription)
    
    let prompt = ''
    
    if (mode === 'single') {
      // 单图模式：强调保持角色特征，只改变服装
      prompt = `
        二次元动漫风格，保持角色面部特征、发型、体型、姿势完全不变，
        将服装更换为：${clothingDescription}，
        保持原有构图和背景，
        高质量，细节丰富，健康向上的画面
      `.replace(/\s+/g, ' ').trim()
    } else if (mode === 'dual') {
      // 双图模式：将图1的服装换为图2的风格
      prompt = `
        二次元动漫风格，将图1角色的服装替换为图2的服装风格，
        保持图1角色的面部、发型、体型、姿势特征不变，
        ${clothingDescription}，
        保持原有构图，
        高质量，细节丰富，健康向上的画面
      `.replace(/\s+/g, ' ').trim()
    }
    
    console.log('🎨 图生图提示词:', prompt)
    return prompt
  }
  
  /**
   * 清理和优化服装描述，避免触发内容审核
   * @param {string} description - 原始描述
   * @returns {string} 清理后的描述
   */
  sanitizeClothingDescription(description) {
    if (!description) return '新服装'
    
    // 常见的可能触发审核的关键词（根据实际情况调整）
    const sensitiveWords = [
      '性感', '暴露', '透明', '裸', '色情',
      '血腥', '暴力', '恐怖', '政治',
      '露', '薄', '紧身'
    ]
    
    let cleaned = description
    
    // 替换敏感词为更中性的表达
    sensitiveWords.forEach(word => {
      if (cleaned.includes(word)) {
        console.warn(`⚠️ 检测到可能触发审核的关键词: ${word}，已优化描述`)
        // 简单替换为空格，实际可以做更智能的替换
        cleaned = cleaned.replace(new RegExp(word, 'g'), '')
      }
    })
    
    // 添加正面引导词
    if (!cleaned.includes('健康') && !cleaned.includes('向上') && !cleaned.includes('正能量')) {
      cleaned = `健康向上，${cleaned}`
    }
    
    return cleaned.trim()
  }

  /**
   * 构建换装提示词 - 保持角色一致性
   */
  buildClothingPrompt(character, clothing) {
    const name = character.name || '角色'
    const personality = character.personality || '友好、开朗'
    const description = character.description || ''
    
    // 解析性格特点
    const personalityTraits = this.parsePersonality(personality)
    
    // 提取角色外观关键词（发型、眼睛、脸型等）
    const appearanceKeywords = this.extractAppearanceKeywords(description)
    
    // 构建服装描述
    const clothingDescription = clothing.description || clothing.name || ''
    const clothingStyle = clothing.style || 'anime style'
    
    // 重点强调：保持角色外观一致，只改变服装
    const prompt = `
      ${name}，
      保持原角色外观特征：${appearanceKeywords.join('，')}，
      性格：${personality}，
      穿着服装：${clothingDescription}，
      只改变服装，其他特征保持不变，
      ${clothingStyle}，
      完整的角色立绘，
      面向镜头，
      单人，
      与角色原始形象保持一致，
      高质量插画
    `.replace(/\s+/g, ' ').trim()
    
    console.log('🎨 换装提示词（保持角色一致）:', prompt)
    
    return prompt
  }
  
  /**
   * 提取角色外观关键词（重点提取不变的特征）
   */
  extractAppearanceKeywords(description) {
    const keywords = []
    const descLower = description.toLowerCase()
    
    // 发型
    if (descLower.includes('长发')) keywords.push('长发')
    if (descLower.includes('短发')) keywords.push('短发')
    if (descLower.includes('双马尾')) keywords.push('双马尾')
    if (descLower.includes('单马尾')) keywords.push('单马尾')
    if (descLower.includes('卷发')) keywords.push('卷发')
    if (descLower.includes('直发')) keywords.push('直发')
    
    // 发色
    if (descLower.includes('黑发')) keywords.push('黑发')
    if (descLower.includes('金发') || descLower.includes('黄发')) keywords.push('金发')
    if (descLower.includes('银发') || descLower.includes('白发')) keywords.push('银发')
    if (descLower.includes('红发')) keywords.push('红发')
    if (descLower.includes('蓝发')) keywords.push('蓝发')
    if (descLower.includes('粉发')) keywords.push('粉发')
    
    // 眼睛
    if (descLower.includes('大眼')) keywords.push('大眼睛')
    if (descLower.includes('蓝眼') || descLower.includes('蓝色眼睛')) keywords.push('蓝色眼睛')
    if (descLower.includes('绿眼') || descLower.includes('绿色眼睛')) keywords.push('绿色眼睛')
    if (descLower.includes('红眼') || descLower.includes('红色眼睛')) keywords.push('红色眼睛')
    
    // 身材
    if (descLower.includes('高挑')) keywords.push('高挑身材')
    if (descLower.includes('娇小')) keywords.push('娇小身材')
    if (descLower.includes('苗条')) keywords.push('苗条身材')
    
    // 特殊特征
    if (descLower.includes('眼镜')) keywords.push('戴眼镜')
    if (descLower.includes('猫耳')) keywords.push('猫耳')
    if (descLower.includes('狐狸耳')) keywords.push('狐狸耳')
    if (descLower.includes('兽耳')) keywords.push('兽耳')
    
    // 如果没有提取到任何关键词，返回基础描述
    if (keywords.length === 0) {
      keywords.push(description || '可爱的角色')
    }
    
    return keywords
  }

  /**
   * 解析性格特点
   */
  parsePersonality(personality) {
    const traits = []
    const personalityLower = personality.toLowerCase()
    
    if (personalityLower.includes('友好') || personalityLower.includes('友善')) traits.push('friendly', 'warm')
    if (personalityLower.includes('开朗') || personalityLower.includes('活泼')) traits.push('cheerful', 'energetic')
    if (personalityLower.includes('温柔') || personalityLower.includes('温和')) traits.push('gentle', 'soft')
    if (personalityLower.includes('幽默') || personalityLower.includes('有趣')) traits.push('humorous', 'playful')
    if (personalityLower.includes('成熟') || personalityLower.includes('稳重')) traits.push('mature', 'calm')
    if (personalityLower.includes('可爱') || personalityLower.includes('萌')) traits.push('cute', 'adorable')
    if (personalityLower.includes('性感') || personalityLower.includes('魅惑')) traits.push('alluring', 'charming')
    if (personalityLower.includes('高冷') || personalityLower.includes('冷淡')) traits.push('cool', 'aloof')
    if (personalityLower.includes('害羞') || personalityLower.includes('腼腆')) traits.push('shy', 'bashful')
    if (personalityLower.includes('自信') || personalityLower.includes('强势')) traits.push('confident', 'strong')
    
    if (traits.length === 0) traits.push('friendly', 'approachable')
    
    return traits
  }

  /**
   * 提取描述关键词
   */
  extractKeywords(description) {
    const keywords = []
    const descLower = description.toLowerCase()
    
    if (descLower.includes('学生') || descLower.includes('学校')) keywords.push('student', 'school uniform')
    if (descLower.includes('职业') || descLower.includes('工作')) keywords.push('professional', 'working')
    if (descLower.includes('魔法') || descLower.includes('巫师')) keywords.push('magical', 'wizard')
    if (descLower.includes('战士') || descLower.includes('战斗')) keywords.push('warrior', 'combat')
    if (descLower.includes('公主') || descLower.includes('贵族')) keywords.push('princess', 'noble')
    if (descLower.includes('猫') || descLower.includes('动物')) keywords.push('cat', 'animal features')
    if (descLower.includes('长发')) keywords.push('long hair')
    if (descLower.includes('短发')) keywords.push('short hair')
    if (descLower.includes('眼镜')) keywords.push('glasses')
    if (descLower.includes('制服')) keywords.push('uniform')
    
    if (keywords.length === 0) keywords.push('unique character design', 'distinctive appearance')
    
    return keywords
  }

  /**
   * 下载图片并转换为Blob
   * @param {string} imageUrl - 图片URL
   * @returns {Promise<Blob>} 图片Blob
   */
  async downloadImageAsBlob(imageUrl) {
    try {
      console.log('📥 开始下载图片:', imageUrl.substring(0, 100))
      
      // 方案1：尝试直接下载（可能遇到CORS）
      try {
        const response = await fetch(imageUrl, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache'
        })
        
        if (response.ok) {
          const blob = await response.blob()
          if (blob.size > 0) {
            console.log('✅ 图片下载成功（CORS），大小:', blob.size, 'bytes', '类型:', blob.type)
            return blob
          }
        }
      } catch (corsError) {
        console.warn('⚠️ CORS下载失败，尝试替代方案:', corsError.message)
      }
      
      // 方案2：使用Image对象 + Canvas转换（绕过CORS限制）
      console.log('🔄 使用Canvas方案下载图片...')
      return await this.downloadImageViaCanvas(imageUrl)
      
    } catch (error) {
      console.error('❌ 下载图片失败:', error)
      console.error('  图片URL:', imageUrl)
      throw new Error(`下载图片失败: ${error.message}`)
    }
  }
  
  /**
   * 通过Canvas下载图片（处理CORS问题）
   * @param {string} imageUrl - 图片URL
   * @returns {Promise<Blob>} 图片Blob
   */
  async downloadImageViaCanvas(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)
          
          canvas.toBlob((blob) => {
            if (blob && blob.size > 0) {
              console.log('✅ Canvas下载成功，大小:', blob.size, 'bytes')
              resolve(blob)
            } else {
              reject(new Error('Canvas转换失败：Blob为空'))
            }
          }, 'image/png', 0.95)
        } catch (error) {
          console.error('❌ Canvas处理失败:', error)
          reject(error)
        }
      }
      
      img.onerror = (error) => {
        console.error('❌ 图片加载失败:', error)
        reject(new Error('图片加载失败，可能是CORS限制'))
      }
      
      // 添加时间戳避免缓存问题
      const urlWithTimestamp = imageUrl + (imageUrl.includes('?') ? '&' : '?') + '_t=' + Date.now()
      img.src = urlWithTimestamp
    })
  }

  /**
   * 保存服装到角色的服装库
   * @param {string} characterId - 角色ID
   * @param {Object} clothing - 服装信息
   * @param {string} generatedImageUrl - 生成的立绘URL
   */
  async saveClothingToLibrary(characterId, clothing, generatedImageUrl) {
    try {
      await this.ensureDB()
      
      // 尝试下载图片并转换为Blob
      let imageBlob = null
      try {
        console.log('📥 正在下载图片为Blob...')
        imageBlob = await this.downloadImageAsBlob(generatedImageUrl)
        console.log('✅ 图片Blob下载成功')
      } catch (downloadError) {
        console.warn('⚠️ 图片Blob下载失败，将只保存URL:', downloadError.message)
        console.log('   💡 图片仍可通过URL直接显示')
        // 不抛出错误，继续保存
      }
      
      const transaction = this.db.transaction(['characterClothing'], 'readwrite')
      const store = transaction.objectStore('characterClothing')

      const clothingData = {
        id: clothing.id || uuidv4(),
        characterId,
        name: clothing.name,
        category: clothing.category || 'full',
        description: clothing.description || '',
        price: clothing.price || 0,
        rarity: clothing.rarity || 'common',
        imageUrl: clothing.imageUrl || '',
        generatedImageUrl: generatedImageUrl, // 原始URL（优先用于显示）
        imageBlob: imageBlob, // 图片Blob（可能为null）
        prompt: this.buildClothingPrompt(clothing.character || {}, clothing),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      return new Promise((resolve, reject) => {
        const request = store.put(clothingData)
        request.onsuccess = () => {
          console.log('✅ 服装已保存到服装库:', clothingData.id)
          console.log('   - 图片URL:', generatedImageUrl ? '✅' : '❌')
          console.log('   - 图片Blob:', imageBlob ? `✅ (${imageBlob.size} bytes)` : '❌ (将使用URL)')
          resolve(clothingData)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 保存服装失败:', error)
      throw error
    }
  }

  /**
   * 获取角色的所有服装
   * @param {string} characterId - 角色ID
   * @returns {Array} 服装列表
   */
  async getCharacterClothing(characterId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterClothing'], 'readonly')
      const store = transaction.objectStore('characterClothing')
      const index = store.index('characterId')

      return new Promise((resolve, reject) => {
        const request = index.getAll(characterId)
        request.onsuccess = () => {
          const clothing = (request.result || []).sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
          )
          resolve(clothing)
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取角色服装失败:', error)
      return []
    }
  }

  /**
   * 删除服装
   * @param {string} clothingId - 服装ID
   */
  async deleteClothing(clothingId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterClothing'], 'readwrite')
      const store = transaction.objectStore('characterClothing')

      return new Promise((resolve, reject) => {
        const request = store.delete(clothingId)
        request.onsuccess = () => {
          console.log('✅ 服装已删除:', clothingId)
          resolve()
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 删除服装失败:', error)
      throw error
    }
  }

  /**
   * 创建Blob URL
   * @param {Blob} blob - 图片Blob
   * @returns {string} Blob URL
   */
  createBlobUrl(blob) {
    if (!blob) return null
    return URL.createObjectURL(blob)
  }

  /**
   * 设置角色当前服装
   * @param {string} characterId - 角色ID
   * @param {string} clothingId - 服装ID（null表示使用默认立绘）
   */
  async setCurrentClothing(characterId, clothingId) {
    try {
      // 更新角色状态中的当前服装ID
      const playCreateStorageService = (await import('./playCreateStorageService.js')).default
      const state = await playCreateStorageService.getCharacterState(characterId)
      
      await playCreateStorageService.saveCharacterState(characterId, {
        ...state,
        currentClothingId: clothingId
      })
      
      // 如果设置了服装，获取服装的立绘Blob并更新角色档案
      if (clothingId) {
        const clothing = await this.getClothingById(clothingId)
        if (clothing) {
          const character = await playCreateStorageService.getCharacterProfile(characterId)
          if (character) {
            // 首次换装时保存原始立绘（如果还没保存的话）
            if (!character.originalAvatar && character.avatar) {
              character.originalAvatar = character.avatar
            }
            if (!character.originalBackground && character.background) {
              character.originalBackground = character.background
            }
            
            // 使用Blob创建URL，如果没有Blob则使用原始URL
            let imageUrl = clothing.generatedImageUrl
            if (clothing.imageBlob) {
              imageUrl = this.createBlobUrl(clothing.imageBlob)
              console.log('✅ 使用Blob URL:', imageUrl.substring(0, 50))
            } else {
              console.log('⚠️ 未找到图片Blob，使用原始URL')
            }
            
            // 更新当前显示的立绘和背景
            character.avatar = imageUrl
            character.background = imageUrl
            console.log('✅ 更新角色立绘:', {
              avatar: character.avatar?.substring(0, 50),
              background: character.background?.substring(0, 50),
              originalAvatar: character.originalAvatar?.substring(0, 50),
              originalBackground: character.originalBackground?.substring(0, 50)
            })
            await playCreateStorageService.saveCharacterProfile(character)
          }
        }
      }
    } catch (error) {
      console.error('❌ 设置当前服装失败:', error)
      throw error
    }
  }

  /**
   * 恢复默认立绘
   * @param {string} characterId - 角色ID
   */
  async restoreDefaultClothing(characterId) {
    try {
      const playCreateStorageService = (await import('./playCreateStorageService.js')).default
      const character = await playCreateStorageService.getCharacterProfile(characterId)
      
      if (character) {
        // 恢复到原始立绘
        if (character.originalAvatar) {
          character.avatar = character.originalAvatar
        }
        if (character.originalBackground) {
          character.background = character.originalBackground
        }
        
        await playCreateStorageService.saveCharacterProfile(character)
        
        // 更新角色状态，清除当前服装ID
        const state = await playCreateStorageService.getCharacterState(characterId)
        await playCreateStorageService.saveCharacterState(characterId, {
          ...state,
          currentClothingId: null
        })
        
        console.log('✅ 已恢复默认立绘')
        return true
      }
      return false
    } catch (error) {
      console.error('❌ 设置当前服装失败:', error)
      throw error
    }
  }

  /**
   * 根据ID获取服装
   * @param {string} clothingId - 服装ID
   */
  async getClothingById(clothingId) {
    try {
      await this.ensureDB()
      const transaction = this.db.transaction(['characterClothing'], 'readonly')
      const store = transaction.objectStore('characterClothing')

      return new Promise((resolve, reject) => {
        const request = store.get(clothingId)
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.error('❌ 获取服装失败:', error)
      return null
    }
  }
}

export default new ClothingService()

